// src/services/cameraService.js - Version améliorée
import { CAMERA_SETTINGS } from '../utils/constants';

class CameraService {
  constructor() {
    this.currentStream = null;
    this.isSupported = null;
    this.permissionStatus = null;
  }

  /**
   * Check if camera is supported
   * @returns {boolean} True if camera is supported
   */
  isCameraSupported() {
    if (this.isSupported === null) {
      // Vérification complète des APIs nécessaires
      this.isSupported = !!(
        navigator.mediaDevices && 
        navigator.mediaDevices.getUserMedia &&
        window.isSecureContext // Vérification HTTPS
      );
    }
    return this.isSupported;
  }

  /**
   * Check if we're in a secure context (HTTPS)
   * @returns {boolean} True if secure context
   */
  isSecureContext() {
    return window.isSecureContext || window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  /**
   * Check camera permission status
   * @returns {Promise<string>} Permission status: 'granted', 'denied', 'prompt'
   */
  async checkPermissionStatus() {
    if (!this.isCameraSupported()) {
      return 'unsupported';
    }

    if (!this.isSecureContext()) {
      return 'insecure';
    }

    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' });
        this.permissionStatus = permission.state;
        return permission.state;
      }
      return 'unknown';
    } catch (error) {
      console.warn('Permission API not supported:', error);
      return 'unknown';
    }
  }

  /**
   * Request camera permissions with improved error handling
   * @returns {Promise<boolean>} True if permission granted
   */
  async requestPermission() {
    const permissionStatus = await this.checkPermissionStatus();
    
    if (permissionStatus === 'unsupported') {
      throw new Error('Caméra non supportée sur cet appareil');
    }

    if (permissionStatus === 'insecure') {
      throw new Error('La caméra nécessite une connexion sécurisée (HTTPS). Veuillez utiliser HTTPS ou localhost.');
    }

    if (permissionStatus === 'denied') {
      throw new Error('Permission caméra refusée. Veuillez autoriser l\'accès dans les paramètres du navigateur.');
    }

    try {
      // Test avec une contrainte minimale
      const testStream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 320, height: 240 } 
      });
      
      // Arrêter immédiatement le test
      testStream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      this._handlePermissionError(error);
      return false;
    }
  }

  /**
   * Start camera stream with improved error handling
   * @param {Object} constraints - Camera constraints (optional)
   * @returns {Promise<MediaStream>} Camera stream
   */
  async startCamera(constraints = CAMERA_SETTINGS) {
    // Vérifications préalables
    const permissionStatus = await this.checkPermissionStatus();
    
    if (permissionStatus === 'denied') {
      throw new Error('Permission caméra refusée. Cliquez sur l\'icône caméra dans la barre d\'adresse pour autoriser.');
    }

    if (!this.isSecureContext()) {
      throw new Error('Connexion non sécurisée. La caméra nécessite HTTPS.');
    }

    try {
      // Stop existing stream if any
      this.stopCamera();

      console.log('🎥 Demande d\'accès à la caméra...', constraints);
      this.currentStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Caméra activée avec succès');
      return this.currentStream;
    } catch (error) {
      console.error('❌ Erreur caméra:', error);
      this._handlePermissionError(error);
    }
  }

  /**
   * Handle permission errors with user-friendly messages
   * @private
   */
  _handlePermissionError(error) {
    console.error('Camera error details:', error);
    
    if (error.name === 'NotAllowedError') {
      throw new Error('Permission caméra refusée. Veuillez autoriser l\'accès à la caméra dans votre navigateur.');
    } else if (error.name === 'NotFoundError') {
      throw new Error('Aucune caméra trouvée sur cet appareil.');
    } else if (error.name === 'NotReadableError') {
      throw new Error('Caméra déjà utilisée par une autre application.');
    } else if (error.name === 'OverconstrainedError') {
      throw new Error('Paramètres de caméra non supportés par votre appareil.');
    } else if (error.name === 'SecurityError') {
      throw new Error('Erreur de sécurité. Veuillez utiliser HTTPS.');
    } else if (error.name === 'AbortError') {
      throw new Error('Opération caméra annulée.');
    } else {
      throw new Error(`Erreur caméra: ${error.message || 'Impossible d\'accéder à la caméra'}`);
    }
  }

  /**
   * Get detailed permission instructions for the user
   * @returns {Object} Instructions by browser
   */
  getPermissionInstructions() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome')) {
      return {
        browser: 'Chrome',
        steps: [
          'Cliquez sur l\'icône caméra dans la barre d\'adresse',
          'Sélectionnez "Toujours autoriser"',
          'Rechargez la page'
        ]
      };
    } else if (userAgent.includes('firefox')) {
      return {
        browser: 'Firefox',
        steps: [
          'Cliquez sur l\'icône bouclier dans la barre d\'adresse',
          'Autorisez l\'accès à la caméra',
          'Rechargez la page'
        ]
      };
    } else if (userAgent.includes('safari')) {
      return {
        browser: 'Safari',
        steps: [
          'Allez dans Safari > Préférences',
          'Onglet Sites web > Caméra',
          'Autorisez ce site'
        ]
      };
    }
    
    return {
      browser: 'Navigateur',
      steps: [
        'Vérifiez les paramètres de votre navigateur',
        'Autorisez l\'accès à la caméra pour ce site',
        'Rechargez la page'
      ]
    };
  }

  /**
   * Get available camera devices with improved error handling
   * @returns {Promise<Array>} Array of video input devices
   */
  async getAvailableCameras() {
    if (!this.isCameraSupported()) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      
      // Log pour debugging
      console.log('📹 Caméras disponibles:', cameras.length);
      cameras.forEach((camera, index) => {
        console.log(`Camera ${index + 1}:`, camera.label || `Caméra ${index + 1}`, camera.deviceId);
      });
      
      return cameras;
    } catch (error) {
      console.error('Error getting camera devices:', error);
      return [];
    }
  }

  /**
   * Stop camera stream
   */
  stopCamera() {
    if (this.currentStream) {
      console.log('🛑 Arrêt de la caméra');
      this.currentStream.getTracks().forEach(track => {
        track.stop();
      });
      this.currentStream = null;
    }
  }

  /**
   * Get current camera stream
   * @returns {MediaStream|null} Current stream or null
   */
  getCurrentStream() {
    return this.currentStream;
  }

  /**
   * Check if camera is currently active
   * @returns {boolean} True if camera is active
   */
  isCameraActive() {
    return !!(this.currentStream && this.currentStream.active);
  }

  /**
   * Switch to specific camera device
   * @param {string} deviceId - Camera device ID
   * @returns {Promise<MediaStream>} New camera stream
   */
  async switchCamera(deviceId) {
    const constraints = {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };

    return await this.startCamera(constraints);
  }

  /**
   * Take a photo from current stream
   * @param {HTMLVideoElement} videoElement - Video element
   * @returns {string} Base64 encoded image data
   */
  takePhoto(videoElement) {
    if (!this.isCameraActive()) {
      throw new Error('Caméra non active');
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    
    return canvas.toDataURL('image/jpeg', 0.8);
  }

  /**
   * Get camera constraints for different scenarios
   * @param {string} scenario - Camera scenario ('barcode', 'photo', 'video')
   * @returns {Object} Camera constraints
   */
  getCameraConstraints(scenario = 'barcode') {
    const baseConstraints = {
      video: {
        facingMode: 'environment' // Use back camera by default
      }
    };

    switch (scenario) {
      case 'barcode':
        return {
          video: {
            facingMode: 'environment',
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            focusMode: 'continuous'
          }
        };
      
      case 'photo':
        return {
          video: {
            facingMode: 'environment',
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 }
          }
        };
      
      case 'selfie':
        return {
          video: {
            facingMode: 'user',
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        };
      
      default:
        return baseConstraints;
    }
  }

  /**
   * Cleanup - stop camera and remove event listeners
   */
  cleanup() {
    this.stopCamera();
  }
}

// Export singleton instance
export const cameraService = new CameraService();
export default cameraService;